import React from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Package, User2, MoreVertical, Copy, Pencil, Trash2, FolderInput } from "lucide-react";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { fmtNumber, fmtDate } from "@/lib/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function OrderCard({ order, onEdit, onDuplicate, onDelete, canWrite, onMoveToFolder }) {
  const navigate = useNavigate();
  const editable = order.can_edit ?? canWrite;
  const shippedTotal = (order.po_details || []).reduce((s, p) => s + (Number(p.shipped_quantity) || 0), 0);

  return (
    <div
      data-testid={`order-card-${order.id}`}
      onClick={() => navigate(`/orders/${order.id}`)}
      className="mf-fade-in group cursor-pointer rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:border-blue-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading truncate text-xl font-bold text-slate-900">{order.buyer_name || "Untitled"}</h3>
          <p className="mt-0.5 text-base text-slate-500">
            {order.department} · Style {order.style_number || "—"}
          </p>
        </div>
        <StatusBadge status={order.status} testId={`order-status-${order.id}`} />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 text-slate-700">
        <div className="flex items-center gap-2.5">
          <Package className="h-5 w-5 shrink-0 text-slate-400" />
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Quantity</p>
            <p className="text-base font-semibold">{fmtNumber(order.order_qty)} pcs</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <Package className="h-5 w-5 shrink-0 text-slate-400" />
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Shipped Quantity</p>
            <p className="text-base font-semibold">{fmtNumber(shippedTotal)} pcs</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <Calendar className="h-5 w-5 shrink-0 text-slate-400" />
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Delivery</p>
            <p className="text-base font-semibold">{fmtDate(order.delivery_date)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <Calendar className="h-5 w-5 shrink-0 text-slate-400" />
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">File Handover</p>
            <p className="text-base font-semibold">{fmtDate(order.file_handover_date)}</p>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-1.5 flex items-center justify-between text-sm">
          <span className="font-medium text-slate-500">Progress</span>
          <span className="font-bold text-slate-700">{order.progress || 0}%</span>
        </div>
        <ProgressBar value={order.progress} status={order.status} />
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <User2 className="h-4 w-4" />
          <span>Created by {order.created_by_name}</span>
        </div>
        {(editable || canWrite || onMoveToFolder) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button
                data-testid={`order-actions-${order.id}`}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {onMoveToFolder && (
                <DropdownMenuItem data-testid={`move-order-${order.id}`} onClick={() => onMoveToFolder(order)}>
                  <FolderInput className="mr-2 h-4 w-4" /> Move to folder
                </DropdownMenuItem>
              )}
              {editable && (
                <DropdownMenuItem data-testid={`edit-order-${order.id}`} onClick={() => onEdit(order)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {canWrite && (
                <DropdownMenuItem data-testid={`duplicate-order-${order.id}`} onClick={() => onDuplicate(order)}>
                  <Copy className="mr-2 h-4 w-4" /> Duplicate
                </DropdownMenuItem>
              )}
              {editable && (
                <DropdownMenuItem
                  data-testid={`delete-order-${order.id}`}
                  onClick={() => onDelete(order)}
                  className="text-rose-600 focus:text-rose-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
